/**
 * @class SlideManager
 * manages slides in a carousel including slide creation, selection, filtering, and dom updates
 */
export default class SlideManager {
	/**
	 * @constructor
	 * @param {Object} ctx - shared module context containing emitter, events, store, etc.
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.allSlides = null;
		_.observer = null;

		_.handlers = {
			selectedIndexChanged: ({ currentIndex }) => {
				_.updateSelectedIndex(currentIndex);
			},
			// debounce the reload so it only happens once per microbatch
			debouncedSlideRefresh: _.ctx.utils.debounce(() => {
				_.reInit();
			}, 4),
		};

		_.init();
	}

	init() {
		this.loadSlides();
		this.bindEvents();
		this.updateSelectedIndex(this.ctx.store.getState().selectedIndex);
	}

	reInit() {
		this.loadSlides();
		this.updateSelectedIndex(this.ctx.store.getState().selectedIndex);
	}

	queryDOM() {
		this.allSlides = Array.from(this.ctx.track.querySelectorAll(':scope > tarot-slide'));
	}

	bindEvents() {
		const _ = this;
		_.ctx.emitter.on(_.ctx.events.store.selectedIndexChanged, _.handlers.selectedIndexChanged);

		// observe when slides are injected into the DOM
		_.observer = new MutationObserver((mutationsList) => {
			for (const mutation of mutationsList) {
				if (mutation.type === 'childList') {
					_.handlers.debouncedSlideRefresh();
					break;
				}
			}
		});

		_.#startObserver();
	}

	/**
	 * Orchestrates all slide management: wraps, queries, filters, indexes, updates state, stores
	 */
	loadSlides() {
		const _ = this;
		_.observer?.disconnect(); // Pause observer during our DOM work

		_.wrapSlides();
		_.queryDOM();
		const activeSlides = _.filterSlides();
		_.resetSlideIndexes(activeSlides);
		_.updateSlideStates(activeSlides);
		_.ctx.store.setSlides(activeSlides);

		_.#startObserver(); // Resume observer
	}

	/**
	 * Filters slides based on filterClass option
	 * @returns {Array} Active slides that pass the filter
	 */
	filterSlides() {
		const filterClass = this.ctx.store.getOptions().filterClass;

		if (!filterClass) {
			return [...this.allSlides];
		}

		return this.allSlides.filter((slide) => slide.classList.contains(filterClass));
	}

	/**
	 * ensures all carousel children are properly wrapped in tarot-slide elements
	 */
	wrapSlides() {
		Array.from(this.ctx.track.children).forEach((child) => {
			if (child.tagName.toLowerCase() !== 'tarot-slide') {
				this.wrapSlide(child);
			}
		});
	}

	/**
	 * Wraps an element in tarot-slide if not already wrapped
	 * Handles both in-DOM elements (replaces in place) and new elements
	 * @param {Element} element - Element to wrap
	 * @returns {Element} The tarot-slide element
	 */
	wrapSlide(element) {
		// Already wrapped - early return
		if (element.tagName.toLowerCase() === 'tarot-slide') {
			return element;
		}

		const wrapper = document.createElement('tarot-slide');
		const parent = element.parentNode;

		// If element is in DOM, replace it in place first
		if (parent) {
			parent.replaceChild(wrapper, element);
		}

		wrapper.appendChild(element);
		return wrapper;
	}

	#startObserver() {
		this.observer?.observe(this.ctx.track, {
			childList: true,
			subtree: false,
		});
	}

	resetSlideIndexes(slides) {
		for (let i = 0, n = slides.length; i < n; ++i) {
			slides[i]._renderIndex = i;
			slides[i]._index = i;
			slides[i].setAttribute('index', i);
		}
	}

	/**
	 * Prepares slides for frame rendering by calculating positions and properties
	 * Includes sophisticated loop positioning to prevent frame gaps during track shifts
	 * Updates the slides in the datastore with fresh positioning data
	 */
	prepSlidesForFrame() {
		const _ = this;
		const slides = _.ctx.store.getSlides();
		const widths = _.ctx.store.getWidths();
		const animation = _.ctx.store.getAnimation();
		const options = _.ctx.store.getOptions();

		// Calculate slide positioning using sophisticated loop logic
		_.updateSlidePositions(animation.trackPosition, slides, widths, options);

		const roundSubPixel = _.ctx.utils.roundSubPixel;

		// Update _trackPosition and _centerPoint properties for all slides
		for (let i = 0, n = slides.length; i < n; ++i) {
			const slide = slides[i]; // This is the actual <tarot-slide> DOM element

			// Calculate _trackPosition: _renderIndex * slideAndGap
			slide._trackPosition = roundSubPixel(slide._renderIndex * widths.slideAndGap);

			// Calculate _centerPoint: _trackPosition + (slide width / 2)
			slide._centerPoint = roundSubPixel(slide._trackPosition + widths.slide / 2);
		}
	}

	/**
	 * Update slide positions based on track position using sophisticated loop logic
	 * Moved from loop-manager.js to ensure sync between track position and slide positioning
	 */
	updateSlidePositions(trackPosition, slides, widths, options) {
		const _ = this;

		// Quick reset if we can't loop
		if (!_.ctx.store.getState().canLoop) {
			_.resetAllSlides(slides);
			return;
		}

		// Get effect buffer requirements for positioning
		const loopBuffer = options.rules.loopBuffer;

		// Get viewport bounds
		const viewportWidth = widths.viewport;
		const slideWidth = widths.slide;
		const gapWidth = widths.gap;
		const slideAndGapWidth = slideWidth + gapWidth;
		const halfSlideWidth = slideWidth / 2;

		// Convert track position to viewport bounds
		const viewportStart = -trackPosition;
		const viewportEnd = viewportStart + viewportWidth;
		const viewportCenter = viewportStart + viewportWidth / 2;

		// Find all slide positions visible in the viewport
		const visibleIndices = new Set();
		const firstIndex = Math.floor(viewportStart / slideAndGapWidth) - 2;
		const lastIndex = Math.ceil(viewportEnd / slideAndGapWidth) + 2;

		for (let i = firstIndex; i <= lastIndex; i++) {
			const slideStart = i * slideAndGapWidth;
			const slideEnd = slideStart + slideWidth;

			if (slideEnd > viewportStart && slideStart < viewportEnd) {
				visibleIndices.add(i);
			}
		}

		if (visibleIndices.size > 0) {
			const indices = Array.from(visibleIndices);
			const minIndex = Math.min(...indices);
			const maxIndex = Math.max(...indices);

			for (let i = 1; i <= loopBuffer.left; i++) {
				visibleIndices.add(minIndex - i);
			}
			for (let i = 1; i <= loopBuffer.right; i++) {
				visibleIndices.add(maxIndex + i);
			}
		}

		// Add remaining slides symmetrically
		const slideCount = slides.length;
		const usedSlides = new Set();
		for (const idx of visibleIndices) {
			const slideIndex = ((idx % slideCount) + slideCount) % slideCount;
			usedSlides.add(slideIndex);
		}

		if (usedSlides.size < slideCount) {
			const indices = Array.from(visibleIndices);
			if (indices.length > 0) {
				const minIndex = Math.min(...indices);
				const maxIndex = Math.max(...indices);
				const remaining = slideCount - usedSlides.size;
				const leftToAdd = Math.floor(remaining / 2);
				const rightToAdd = remaining - leftToAdd;

				for (let i = 1; i <= leftToAdd; i++) {
					visibleIndices.add(minIndex - i);
				}
				for (let i = 1; i <= rightToAdd; i++) {
					visibleIndices.add(maxIndex + i);
				}
			}
		}

		// CRITICAL FIX: Handle position overflow when we need more positions than slides
		// This happens during drag transitions when partial visibility on both edges
		// causes visibleIndices + buffers to exceed slideCount
		if (visibleIndices.size > slideCount) {
			// Sort positions by distance to viewport center (closest first)
			const sortedPositions = Array.from(visibleIndices).sort((a, b) => {
				const centerA = a * slideAndGapWidth + halfSlideWidth;
				const centerB = b * slideAndGapWidth + halfSlideWidth;
				const distA = Math.abs(centerA - viewportCenter);
				const distB = Math.abs(centerB - viewportCenter);
				return distA - distB;
			});

			// Keep only the closest slideCount positions
			visibleIndices.clear();
			for (let i = 0; i < slideCount; i++) {
				visibleIndices.add(sortedPositions[i]);
			}
		}

		// Apply render indices
		const assignments = new Map();

		for (const logicalIndex of visibleIndices) {
			const slideIndex = ((logicalIndex % slideCount) + slideCount) % slideCount;
			assignments.set(slides[slideIndex], logicalIndex);
		}

		// Update slide render indices
		for (const slide of slides) {
			const targetIndex = assignments.get(slide);
			if (targetIndex !== undefined) {
				slide._renderIndex = targetIndex;
			}
		}
	}

	/**
	 * Reset all slides to their natural indices (no looping)
	 */
	resetAllSlides(slides) {
		for (let i = 0; i < slides.length; i++) {
			slides[i]._renderIndex = i;
		}
	}

	/**
	 * Updates DOM state attributes on slides
	 * @param {Array} activeSlides - Slides that should be active
	 */
	updateSlideStates(activeSlides) {
		const activeSet = new Set(activeSlides);

		this.allSlides.forEach((slide) => {
			if (activeSet.has(slide)) {
				slide.setAttribute('state', 'active');
			} else {
				slide.setAttribute('state', 'disabled');
			}
		});
	}

	addSlide(element, index) {
		const _ = this;

		// Handle string input by creating element first
		let el = element;
		if (typeof element === 'string') {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = element.trim();
			el = tempDiv.firstChild;
		}

		// Wrap if needed (early return if already tarot-slide)
		const newSlide = _.wrapSlide(el);

		// Insert into DOM - observer will trigger loadSlides via debounce
		const currentSlides = _.ctx.store.getSlides();
		if (typeof index === 'number' && index >= 0 && index < currentSlides.length) {
			_.ctx.track.insertBefore(newSlide, currentSlides[index]);
		} else {
			_.ctx.track.appendChild(newSlide);
		}
	}

	removeSlide(index) {
		const activeSlides = this.ctx.store.getSlides();
		if (index < 0 || index >= activeSlides.length) return;
		// Remove from DOM - observer will trigger loadSlides via debounce
		activeSlides[index].remove();
	}

	/**
	 * Updates _selected property on slide objects (not DOM)
	 * @param {number} newIndex - The newly selected index
	 */
	updateSelectedIndex(newIndex) {
		const slides = this.ctx.store.getSlides();
		for (let i = 0, n = slides.length; i < n; ++i) {
			slides[i]._selected = slides[i]._index === newIndex;
		}
	}

	destroy() {
		const _ = this;

		_.ctx.emitter.off(_.ctx.events.store.selectedIndexChanged, _.handlers.selectedIndexChanged);

		if (_.observer) {
			_.observer.disconnect();
			_.observer = null;
		}

		// cancel debounced function if it has a cancel method
		if (_.handlers.debouncedSlideRefresh?.cancel) {
			_.handlers.debouncedSlideRefresh.cancel();
		}
		_.allSlides = null;
	}
}
